import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  CheckCircle, 
  X, 
  Eye, 
  RotateCcw,
  Clock,
  Database,
  Shield
} from 'lucide-react';

const ChatConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  confirmationData, 
  onConfirm, 
  onCancel,
  loading = false 
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Auto-expire countdown
  React.useEffect(() => {
    if (confirmationData?.expires_in_minutes && isOpen) {
      const totalSeconds = confirmationData.expires_in_minutes * 60;
      setCountdown(totalSeconds);
      
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [confirmationData, isOpen, onClose]);

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderPreviewData = (preview) => {
    if (!preview || typeof preview !== 'object') return null;
    
    return (
      <div className="space-y-4">
        {Object.entries(preview).map(([tableName, records]) => {
          if (!Array.isArray(records) || records.length === 0) return null;
          
          return (
            <div key={tableName} className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3 capitalize">
                {tableName.replace('_', ' ')} ({records.length} records)
              </h4>
              
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {records.map((record, index) => (
                  <div key={index} className="bg-white p-2 rounded border text-sm">
                    {Object.entries(record).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                        <span className="font-medium text-gray-900 truncate ml-2">
                          {value?.toString() || 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen || !confirmationData) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-orange-50 border-b border-orange-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Database Operation
                  </h3>
                  <p className="text-sm text-orange-700">
                    This action will modify your CRM data
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {countdown && (
                  <div className="flex items-center space-x-1 text-orange-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-mono">{formatCountdown(countdown)}</span>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Operation Description */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Operation Details:</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {confirmationData.description}
                </pre>
              </div>
            </div>

            {/* Affected Records Count */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 text-sm">
                <Database className="w-4 h-4 text-blue-600" />
                <span className="text-gray-600">Records affected:</span>
                <span className="font-semibold text-gray-900">
                  {confirmationData.affected_count}
                </span>
              </div>
            </div>

            {/* Preview Toggle */}
            {confirmationData.preview && (
              <div className="mb-6">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {showPreview ? 'Hide' : 'Show'} Preview Data
                  </span>
                </button>
                
                <AnimatePresence>
                  {showPreview && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 overflow-hidden"
                    >
                      {renderPreviewData(confirmationData.preview)}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Safety Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-blue-900 mb-1">Safety Features Active</h5>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Automatic backup will be created before execution</li>
                    <li>• You can restore data if needed</li>
                    <li>• Operation will be logged for audit trail</li>
                    <li>• Changes can be reviewed before final confirmation</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-red-900 mb-1">Important Warning</h5>
                  <p className="text-sm text-red-800">
                    This operation will permanently modify your CRM data. 
                    While backups are created automatically, please ensure you want to proceed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <RotateCcw className="w-4 h-4" />
                <span>Operation ID: {confirmationData.operation_id?.slice(0, 8)}...</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={onCancel}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  onClick={onConfirm}
                  disabled={loading}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Executing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Confirm & Execute</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChatConfirmationDialog;